/**
 * Botão "Instalar o app" do rodapé da lateral — só no site.
 *
 * O que ele oferece é o instalador do app desktop que já está publicado em
 * `/desktop-updates`, o mesmo que o electron-updater consome. A versão não é
 * fixada aqui de propósito: ela muda a cada publicação, e quem sabe qual é a
 * atual é o `latest.yml` no servidor (ver `/api/desktop/latest`).
 *
 * O botão some sozinho em três casos, e em todos some por ser honesto:
 *  - dentro do próprio app desktop, onde não há nada a instalar;
 *  - fora do Windows, porque o instalador publicado é um `.exe` x64 e não há
 *    service worker no projeto para oferecer instalação como PWA;
 *  - quando não há release publicado (desenvolvimento, por exemplo).
 */

import { useEffect, useState } from "react";
import { DownloadIcon } from "@/components/icons";
import { C, FONT_MONO, hexA } from "@/theme";

type Release = { version: string; url: string; size: number | null };

/** `true` quando faz sentido oferecer o instalador nesta máquina. */
function podeInstalar(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (window.lfrcordDesktop?.isElectron === true) return false;
  return /Windows/i.test(navigator.userAgent);
}

/** "97 MB". Sem casa decimal: o número exato não muda decisão nenhuma. */
function tamanho(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes) || bytes <= 0) return "";
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

function useRelease(): Release | null {
  const [release, setRelease] = useState<Release | null>(null);

  useEffect(() => {
    if (!podeInstalar()) return;

    let vivo = true;
    fetch("/api/desktop/latest", { headers: { Accept: "application/json" } })
      .then(resposta => (resposta.ok ? resposta.json() : null))
      .then((dados: unknown) => {
        if (!vivo || typeof dados !== "object" || dados === null) return;
        const corpo = dados as Record<string, unknown>;
        if (corpo.available !== true) return;

        const url = typeof corpo.url === "string" ? corpo.url : "";
        const version = typeof corpo.version === "string" ? corpo.version : "";
        // A resposta é do nosso próprio servidor, mas o valor acaba num `href`:
        // amarrar ao prefixo esperado custa uma linha e fecha a porta.
        if (!version || !url.startsWith("/desktop-updates/")) return;

        setRelease({
          version,
          url,
          size: typeof corpo.size === "number" ? corpo.size : null,
        });
      })
      .catch(() => {
        // Sem release publicado ou sem rede: o botão apenas não aparece.
      });

    return () => {
      vivo = false;
    };
  }, []);

  return release;
}

export function InstallAppButton() {
  const release = useRelease();
  if (!release) return null;

  const peso = tamanho(release.size);

  return (
    <div
      style={{
        padding: "9px 12px",
        borderTop: `1px solid ${C.lineSoft}`,
        background: C.panelFoot,
      }}
    >
      <a
        className="hov-bright-108"
        href={release.url}
        download
        title={`Baixar o LFRCORD para Windows${peso ? ` · ${peso}` : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          borderRadius: 8,
          background: hexA(C.accent, 0.14),
          border: `1px solid ${hexA(C.accent, 0.4)}`,
          color: C.blue,
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        <DownloadIcon size={14} style={{ flex: "0 0 auto" }} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Instalar o app
        </span>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 9.5,
            color: C.textLabel,
            flex: "0 0 auto",
          }}
        >
          {release.version}
        </span>
      </a>
    </div>
  );
}
